import React from "react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { IoLocationOutline } from "react-icons/io5";
import { PiMapPinArea } from "react-icons/pi";
import { MdOutlineKeyboardDoubleArrowUp } from "react-icons/md";
import { useTranslation } from "react-i18next";

const Counter = ({ from, to, suffixKey = "", t }) => {
  const [count, setCount] = useState(from);
  const [start, setStart] = useState(false);

  useEffect(() => {
    if (!start) return;
    const interval = setInterval(() => {
      setCount((prev) => {
        if (prev < to) return prev + Math.ceil(to / 100); // Adjusted increment for smoother animation
        clearInterval(interval);
        return to;
      });
    }, 30); // Adjusted interval for smoother animation
    return () => clearInterval(interval);
  }, [to, start]);

  return (
    <motion.span 
      className="text-2xl md:text-3xl font-light text-verde_scuro"
      whileInView={() => setStart(true)}
      viewport={{ once: true }} // Ensure counter starts only once when visible
    >
      {count}{suffixKey && suffixKey}
    </motion.span>
  );
};

const VignetoPage =  () => {
    const { t } = useTranslation();

    const vineyardSectionsData = t("vigneto.sections", { returnObjects: true }) || [];
    const grapeVarietiesData = t("vigneto.grape_varieties", { returnObjects: true }) || [];

    const sectionIcons = [
        <IoLocationOutline />,
        <PiMapPinArea />,
        <MdOutlineKeyboardDoubleArrowUp />
    ];

    // Original numeric data for counters, as these shouldn't be translated
    const counterNumericData = [
        { from: 0, to: 45, to2: 75391, to3: 9, to4: 33028 }, // Coordinates
        { from: 0, to: 40000 }, // Superficie
        { from: 0, to: 320 }  // Altitudine
    ];

    return (
        <div>
          <h1 className="text-4xl font-light text-gray-800 titolo mb-4 text-center">
            {t("vigneto.titolo_pagina")}
          </h1>

          {vineyardSectionsData.map((section, index) => (
            <motion.div
              key={index}
              className={`flex flex-col md:flex-row items-center py-8 ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img 
                src={index === 0 ? "/beverahills_vigneto_9.webp" : index === 1 ? "/beverahills_vigneto_10.jpg" : "/beverahills_uva_1.webp"} 
                alt={section.alt_vigneto} 
                className="w-full md:w-1/2 rounded-2xl shadow-lg" 
              />
              <div className="w-full md:w-1/2 p-6 md:ml-6 md:mr-6 shadow-md shadow-verde_scuro rounded-xl">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
                  <p className="text-lg text-gray-700 font-medium whitespace-pre-line">{section.text}</p>
                </div>
                <div className="mt-8 p-6 bg-gray-100 rounded-xl shadow-md flex flex-col items-center">
                    <h3 className="text-lg md:text-xl font-medium text-gray-700 flex items-center">
                        {sectionIcons[index]}   {section.counter_title}
                    </h3>
                    {index === 0 && ( // Specific counter for coordinates
                        <span className="text-sm md:text-xl font-light text-verde_scuro">
                            <Counter t={t} from={counterNumericData[0].from} to={counterNumericData[0].to} />.
                            <Counter t={t} from={counterNumericData[0].from} to={counterNumericData[0].to2} />,  
                            <Counter t={t} from={counterNumericData[0].from} to={counterNumericData[0].to3} />.
                            <Counter t={t} from={counterNumericData[0].from} to={counterNumericData[0].to4} />
                        </span>
                    )}
                    {index === 1 && ( // Counter for superficie
                        <Counter t={t} from={counterNumericData[1].from} to={counterNumericData[1].to} suffixKey={section.counter_suffix} />
                    )}
                    {index === 2 && ( // Counter for altitudine
                        <Counter t={t} from={counterNumericData[2].from} to={counterNumericData[2].to} suffixKey={section.counter_suffix} />
                    )}
                </div>
              </div>
            </motion.div>
          ))}
      
          <div className="mt-12 text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">{t("vigneto.varieta_uva_titolo")}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {grapeVarietiesData.map((group, index) => (
                <div 
                    key={index} 
                    className={`shadow-md ${index === 0 ? "shadow-lime-800" : "shadow-indigo-950"} p-6 rounded-xl`}
                >
                  <div className="flex items-center justify-center mb-8">
                    <h3 className="text-2xl font-semibold text-gray-800 underline ">{group.category}</h3>
                    <img 
                        src={index === 0 ? "/icona_uva_bianca.png" : "/icona_uva_rossa.png"} 
                        alt={group.alt_icon} 
                        className="w-8 h-8 ml-2" 
                    />
                  </div>
                  <ul className="text-lg text-gray-800">
                    {group.varieties.map((variety, i) => (
                      <li key={i} className="mb-2">{variety}</li> // Variety names are often proper nouns, might not need translation. If they do, t(variety)
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
    );
};

export default VignetoPage;